import {
  SECTION_MATERIALS,
  roofsWithout,
  sectionFields,
  sectionSquares,
  sectionWarnings,
  templatesForPath,
  type RoofLayout,
  type SectionMaterial,
  type RoofSection,
  type RoofTemplate,
} from "@roof-hawk/core";
import { useState } from "react";

import { t } from "../../i18n";
import { PATH_CLASS } from "./paths";

/**
 * "Is any part of this roof a different material?"
 *
 * Most roofs are one thing and this stays a single line of text. Some are not:
 * a metal porch on a shingle house, a TPO dormer, a lean-to in panel.
 * Jonathon, 2026-09-16: "some roofs have shingles and metal etc on the same
 * roof. we need to be able to select whether other parts of the roof have
 * metal or tpo and then proceed accordingly."
 *
 * The measuring service reports the building as one roof, so every square of
 * that porch is already in the 81 the report gives. Pricing the porch as well
 * without taking it out charges the customer for it twice and buys shingles to
 * lay underneath a metal roof. So each area is described here — what it is,
 * which package covers it, and its own measurements — and what the packages
 * above are generated from is the roof less these areas. The arithmetic is
 * `roofsWithout` in packages/core; this screen only asks.
 *
 * Nothing is stored. An area describes one estimate, not the building: the
 * roof was measured once and stays as measured, and what this produced is
 * visible afterwards as the lines and the orders it wrote.
 */
export function RoofSectionsField({
  sections,
  onChange,
  templates,
  roofs,
}: {
  sections: readonly RoofSection[];
  onChange: (sections: RoofSection[]) => void;
  templates: readonly RoofTemplate[];
  roofs: readonly RoofLayout[];
}) {
  /*
   * What is in the boxes, as typed.
   *
   * The section carries numbers, and a number cannot hold "8." on the way to
   * "8.5" — storing the parsed value straight back into the input deletes the
   * decimal point as fast as it is typed.
   */
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const add = () =>
    onChange([
      ...sections,
      {
        id: `section-${Date.now()}-${sections.length}`,
        name: "",
        material: "Metal",
        templateId: "",
        layoutId: roofs[0]?.id ?? null,
        values: {},
      },
    ]);

  const update = (id: string, patch: Partial<RoofSection>) =>
    onChange(
      sections.map((section) =>
        section.id === id ? { ...section, ...patch } : section,
      ),
    );

  const remove = (id: string) =>
    onChange(sections.filter((section) => section.id !== id));

  if (sections.length === 0) {
    return (
      <div className="field">
        <label>{t("Is any part of this roof a different material?")}</label>
        <div>
          <button type="button" className="btn-small" onClick={add}>
            {t("Part of this roof is metal, TPO or something else")}
          </button>
        </div>
        <span className="na-hint">
          {t(
            "A metal porch, a flat dormer, a lean-to. Describe it and it is priced as itself — and taken off the roof the packages above are worked out from, so the same square is never sold twice.",
          )}
        </span>
      </div>
    );
  }

  const warnings = sectionWarnings(roofs, sections);
  const rest = roofsWithout(roofs, sections);
  const restSquares = rest.reduce(
    (total, roof) => total + Number(roof.squares ?? 0),
    0,
  );

  return (
    <div className="field">
      <label>{t("The parts of this roof in another material")}</label>

      {sections.map((section) => {
        const offered = templatesForPath(templates, section.material);
        const fields = sectionFields(section.material);
        return (
          <div
            className={`sectioncard ${PATH_CLASS[section.material]}`}
            key={section.id}
          >
            <div className="sectionhead">
              <input
                aria-label={t("Area name")}
                placeholder={t("Front porch")}
                value={section.name}
                onChange={(e) => update(section.id, { name: e.target.value })}
              />
              <button
                type="button"
                className="btn-small danger"
                onClick={() => remove(section.id)}
              >
                {t("Remove")}
              </button>
            </div>

            <div className="fgrid">
              <div className="field">
                <label>{t("Material")}</label>
                <select
                  aria-label={t("Material for {area}", {
                    area: section.name || t("this area"),
                  })}
                  value={section.material}
                  onChange={(e) => {
                    const material = e.target.value as SectionMaterial;
                    // A package on the old path is not a package on the new
                    // one, so it is dropped rather than left reading right.
                    const keeps = templatesForPath(templates, material).some(
                      (t) => t.id === section.templateId,
                    );
                    update(section.id, {
                      material,
                      templateId: keeps ? section.templateId : "",
                    });
                  }}
                >
                  {SECTION_MATERIALS.map((path) => (
                    <option key={path} value={path}>
                      {t(path)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>
                  {t("Package")} <span className="req">*</span>
                </label>
                <select
                  aria-label={t("Package for {area}", {
                    area: section.name || t("this area"),
                  })}
                  value={section.templateId}
                  onChange={(e) =>
                    update(section.id, { templateId: e.target.value })
                  }
                >
                  <option value="">{t("Pick a package")}</option>
                  {offered.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.package_name?.trim()
                        ? `${t.package_name} — ${t.name}`
                        : t.name}
                    </option>
                  ))}
                </select>
                {offered.length === 0 && (
                  <span className="na-hint">
                    {t(
                      "No {material} package is built out yet. Build one under Tools → Templates.",
                      { material: t(section.material).toLowerCase() },
                    )}
                  </span>
                )}
              </div>

              {/* Only worth asking when the job has more than one roof: the
                  measurements come off the layout this area is part of. */}
              {roofs.length > 1 && (
                <div className="field">
                  <label>{t("Measured out of")}</label>
                  <select
                    aria-label={t("Which roof {area} is part of", {
                      area: section.name || t("this area"),
                    })}
                    value={section.layoutId ?? ""}
                    onChange={(e) =>
                      update(section.id, { layoutId: e.target.value || null })
                    }
                  >
                    {roofs.map((roof) => (
                      <option key={roof.id} value={roof.id}>
                        {roof.name || t("Untitled layout")}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {fields.map((field) => {
                const key = `${section.id}:${field.key}`;
                const stored = section.values[field.key];
                return (
                  <div className="field" key={field.key}>
                    <label>{t(field.label)}</label>
                    <input
                      inputMode="decimal"
                      placeholder={t(field.label)}
                      value={
                        drafts[key] ?? (stored == null ? "" : String(stored))
                      }
                      onChange={(e) => {
                        const typed = e.target.value;
                        setDrafts((was) => ({ ...was, [key]: typed }));
                        const parsed = Number(typed.trim());
                        update(section.id, {
                          values: {
                            ...section.values,
                            [field.key]:
                              typed.trim() === "" || !Number.isFinite(parsed)
                                ? null
                                : parsed,
                          },
                        });
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div>
        <button type="button" className="btn-small" onClick={add}>
          {t("Add another area")}
        </button>
      </div>

      {/* What the packages above are now generated from. The rep entered the
          porch; this is the number that proves it came off the house. */}
      <span className="na-hint">
        {t(
          "The packages above will be generated from the rest of the roof — {squares} SQ, once {areas} is taken out.",
          {
            squares: Math.round(restSquares * 100) / 100,
            areas: sections
              .map((section) =>
                t("{area} ({squares} SQ)", {
                  area: section.name.trim() || t("this area"),
                  squares: sectionSquares(section),
                }),
              )
              .join(t(" and ")),
          },
        )}
      </span>

      {warnings.length > 0 && (
        <div className="config-warn" role="status" style={{ marginTop: 8 }}>
          {warnings.map((warning) => (
            <div key={warning}>{t(warning)}</div>
          ))}
        </div>
      )}
    </div>
  );
}
